import { useEffect } from 'react';
import { useIonRouter } from '@ionic/react';
import { useLocation } from 'react-router-dom';
import { useShareWizardContext } from '@/contexts/ShareWizardContext';
import type { PersistedWizardState } from '@/modules/share/session_storage';
import { Destinations } from '@/modules/share/navigation_logic';
import type { ShareDestination } from '@/modules/share/types';
import { MePrFoService } from '@/services/meprfo';

/**
 * Whether the cursor points at the final leg in the queue. Shared so the
 * redirect components and the hook agree on share-confirm vs final-share-confirm.
 */
export function isEndOfSendQueue(
  sendQueueCursor: number,
  sendQueueLength: number
): boolean {
  return sendQueueLength > 0 && sendQueueCursor === sendQueueLength - 1;
}

export interface LegTapDecision {
  /** The tap is the sender returning via their own in-progress leg link. */
  isImpliedConfirm: boolean;
  /** Which sending destination the sender lands on when isImpliedConfirm. */
  sendingDestination: ShareDestination;
}

/**
 * Decide how a /b/{legId} (or /share?legId=) tap should be routed, by
 * comparing the tapped legId against the in-progress leg restored from
 * storage. This is the common logic shared by LegRedirect and
 * ShareWizardRedirect (and mirrors useImpliedConfirm's in-context detection).
 *
 * A match means the sender is returning from SMS — an implied confirmation. A
 * non-match (including no in-progress leg) means a recipient tapped the link.
 */
export function decideLegTap(
  legId: string | null,
  persisted: PersistedWizardState | null
): LegTapDecision {
  const inProgressLegId = persisted?.inProgressLegId ?? null;
  const isImpliedConfirm = !!legId && legId === inProgressLegId;
  const isLastBatch = isEndOfSendQueue(
    persisted?.sendQueueCursor ?? 0,
    persisted?.sendQueue.length ?? 0
  );
  return {
    isImpliedConfirm,
    sendingDestination: isLastBatch ? Destinations.sent : Destinations.send,
  };
}

interface UseImpliedConfirmOptions {
  /**
   * When true (ShareConfirmStep), an implied confirm on the final share moves
   * the sender forward to the `sent` destination. When false
   * (FinalShareConfirmStep, which is already there), it only marks the leg sent
   * in place.
   */
  navigateToFinalShareConfirm?: boolean;
}

/**
 * Implied-confirm detection shared by ShareConfirmStep and FinalShareConfirmStep.
 *
 * If the sender taps their own leg link to return to Snowball,
 * ShareWizardRedirect carries the legId in the URL. When that legId matches
 * the in-progress one restored from storage, we treat the tap as confirmation
 * that the leg was sent: mark it sent on the backend and flip the display
 * flag, skipping the manual swear-to-return flow.
 *
 * Implied confirm only marks the leg sent and flips `impliedConfirm`. The
 * local bookkeeping (awareness cache, confirmed count) is deferred to the
 * proceed handlers — handleConfirmAndOpenNext for an intermediate leg,
 * handleConfirmFinalShare for the last — so it runs exactly once when the user
 * moves forward.
 */
export function useImpliedConfirm({
  navigateToFinalShareConfirm = false,
}: UseImpliedConfirmOptions = {}): void {
  const {
    sendQueue,
    sendQueueCursor,
    inProgressLegId,
    impliedConfirm,
    setImpliedConfirm,
    isLoadingLegState,
    nav,
  } = useShareWizardContext();

  // Read the query string from Ionic's routeInfo first. When the page is reached
  // via a client-side <Redirect> inside IonRouterOutlet, React Router's
  // useLocation().search arrives empty — Ionic carries the query string on
  // routeInfo instead. Falling back to location.search covers a hard page load
  // (refresh / fresh tab) where the document URL already has the query string.
  const ionRouter = useIonRouter();
  const location = useLocation();
  const rawSearch = ionRouter.routeInfo?.search || location.search || '';
  const legIdInUrl = new URLSearchParams(rawSearch.replace(/^\?/, '')).get(
    'legId'
  );

  useEffect(() => {
    // Wait for context to finish hydrating from storage before comparing.
    // Guard with impliedConfirm to prevent re-firing after it's set.
    if (impliedConfirm || isLoadingLegState) {
      return;
    }
    if (!legIdInUrl || legIdInUrl !== inProgressLegId) {
      return;
    }
    if (!sendQueue[sendQueueCursor]) {
      return;
    }
    const isLastBatch = isEndOfSendQueue(sendQueueCursor, sendQueue.length);
    MePrFoService.markLegAsSent(inProgressLegId).then(() => {
      setImpliedConfirm(true);
      if (navigateToFinalShareConfirm && isLastBatch) {
        nav.goTo(Destinations.sent, 'forward');
      }
    });
  }, [
    impliedConfirm,
    isLoadingLegState,
    legIdInUrl,
    inProgressLegId,
    sendQueue,
    sendQueueCursor,
    nav,
    setImpliedConfirm,
    navigateToFinalShareConfirm,
  ]);
}
